import { Icon, Menu, Portal } from '@chakra-ui/react';
import { IRateReqSchemaUI } from '@monetize/types/app';
import { orderObjectsBy } from '@monetize/utils/core';
import { MdSort } from 'react-icons/md';
import { MButton } from '../../../components/Monetize';

const rateSortItems: Array<{
  label: string;
  value: Extract<keyof IRateReqSchemaUI, string | 'number'>;
}> = [
  { label: 'Alphabetically', value: 'name' },
  { label: 'Currency', value: 'currency' },
  { label: 'Frequency', value: 'billingFrequencyInMonths' },
  { label: 'Timing', value: 'subscriptionTiming' },
  { label: 'Start Date', value: 'startDate' },
  { label: 'End Date', value: 'endDate' },
] as const;

export type RateSortKey = (typeof rateSortItems)[number]['value'];

export function sortRatesByFilterValue(
  rates: any[],
  sortKey: RateSortKey,
  order: 'asc' | 'desc',
) {
  return orderObjectsBy(
    rates,
    [sortKey, 'name', 'createDate'],
    [order, order, order],
  );
}

interface OfferingFormRateSortButtonProps {
  selectedItem: RateSortKey;
  order: 'asc' | 'desc';
  onChange: (value: RateSortKey, order: 'asc' | 'desc') => void;
}

/**
 * Sort menu button for offering rates
 */
export const OfferingFormRateSortButton = ({
  selectedItem,
  order,
  onChange,
}: OfferingFormRateSortButtonProps) => {
  const selectedItemLabel = (
    rateSortItems.find((item) => item.value === selectedItem) ??
    rateSortItems[0]
  ).label;

  return (
    <Menu.Root lazyMount unmountOnExit>
      {/* `unstyled` — the menu recipe's trigger styles (plain text color and
       * weight, its own hover) would otherwise override the tertiary variant
       * on the button. v2's `MenuButton as={MButton}` never applied them. */}
      <Menu.Trigger asChild unstyled>
        <MButton minW="135px" size="sm" variant="tertiary">
          <Icon asChild>
            <MdSort />
          </Icon>
          {selectedItemLabel}
        </MButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            {/* Controlled: Ark's radio group ignores `defaultValue`. Re-picking
                the current item is skipped, as v2's option group did. */}
            <Menu.RadioItemGroup
              value={order}
              onValueChange={({ value }) => {
                if (value !== order) {
                  onChange(selectedItem, value as 'asc' | 'desc');
                }
              }}
            >
              <Menu.ItemGroupLabel>Order</Menu.ItemGroupLabel>
              <Menu.RadioItem value="asc">
                <Menu.ItemIndicator />
                Ascending
              </Menu.RadioItem>
              <Menu.RadioItem value="desc">
                <Menu.ItemIndicator />
                Descending
              </Menu.RadioItem>
            </Menu.RadioItemGroup>
            <Menu.RadioItemGroup
              value={selectedItem}
              onValueChange={({ value }) => {
                if (value !== selectedItem) {
                  onChange(value as RateSortKey, order);
                }
              }}
            >
              <Menu.ItemGroupLabel>Sort By</Menu.ItemGroupLabel>
              {rateSortItems.map(({ label, value }) => (
                <Menu.RadioItem key={value} value={value}>
                  <Menu.ItemIndicator />
                  {label}
                </Menu.RadioItem>
              ))}
            </Menu.RadioItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};
